import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormCard2 } from "@/components/FormCard2";
import { Check, Lock, Mail, UserRound } from "lucide-react";
import { cn } from "@/util/cn";
import { useUser } from "@/queries/user/useUser";
import {
  updateUserProfileInputSchema,
  useUpdateUserProfile,
  phoneNumbersSchema,
} from "@/queries/user/useUpdateUserProfile";
import { z } from "zod";
import { CustomPhoneInput } from "@/components/ui/CustomPhoneInput";
import {
  EmailType,
  OwnerType,
  PhoneNumberType,
} from "@/api/SolarCloudApi";
import {
  findDefaultEmail,
  findDefaultPhoneNumber,
  updateEmails,
  updatePhoneNumbers,
} from "@/util/user";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { FieldValues, UseFormReturn, useWatch } from "react-hook-form";
import { usePaymentGatekeeperStore } from "./paymentGatekeeper";
import { scrollToFirstInvalidField } from "./formValidation";
import { useDebouncedAutosave } from "./useDebouncedAutosave";
import {
  getPaymentDetailsValidity,
  isPaymentDetailsComplete,
  paymentDetailsSchema,
} from "./paymentDetails";
import { useBuyPanelsStore } from "../store";

const optionalPhoneNumberFormSchema = z.object({
  uuid: z.string().optional(),
  type: z.nativeEnum(PhoneNumberType).optional(),
  number: z.string().optional(),
});

const userFormSchema = paymentDetailsSchema.extend({
  phoneNumber: optionalPhoneNumberFormSchema,
});

type TUserForm = z.infer<typeof userFormSchema>;

export default function UserForm({
  onSuccess,
}: {
  onSuccess: (uuid: string) => void | Promise<void>;
}) {
  const { data: user, isLoading: isLoadingUser } = useUser();
  const ownerType = useBuyPanelsStore((state) => state.data.ownerType);

  const defaultValues = useMemo<TUserForm>(() => {
    const defaultPhoneNumber = findDefaultPhoneNumber(user);
    const defaultEmail = findDefaultEmail(user);
    const defaultEmailAddress = paymentDetailsSchema.shape.email.safeParse(
      defaultEmail?.address
    ).success
      ? defaultEmail?.address!.trim()
      : "";

    return {
      givenName: user?.givenName ?? "",
      familyName: user?.familyName ?? "",
      email: defaultEmailAddress,
      phoneNumber: {
        number: defaultPhoneNumber?.number ?? "",
        type: defaultPhoneNumber?.type ?? PhoneNumberType.DEFAULT,
        uuid: defaultPhoneNumber?.uuid,
      },
    } as TUserForm;
  }, [user]);

  const updateUserProfile = useUpdateUserProfile();
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());

  const saveProfile = useCallback(
    async (data: TUserForm, syncOwner: boolean) => {
      const runSave = async () => {
        const phoneNumber = data.phoneNumber?.number?.trim();
        const parsedPhoneNumber = phoneNumber
          ? phoneNumbersSchema.safeParse({
            ...data.phoneNumber,
            number: phoneNumber,
          })
          : null;
        const userNumbers = parsedPhoneNumber?.success
          ? updatePhoneNumbers(user, parsedPhoneNumber.data)
          : undefined;

        const defaultEmail = findDefaultEmail(user);
        const emailAddress = data.email.trim();
        const emails = updateEmails(user, {
          uuid: defaultEmail?.uuid,
          type: defaultEmail?.type ?? EmailType.DEFAULT,
          address: emailAddress,
        });

        const updatedUser = {
          givenName: data.givenName.trim(),
          familyName: data.familyName.trim(),
          emails,
          ...(userNumbers ? { phoneNumbers: userNumbers } : {}),
        };

        const parsedUser = updateUserProfileInputSchema.parse(updatedUser);
        await updateUserProfile.mutateAsync(parsedUser);

        if (syncOwner && user?.uuid && ownerType !== OwnerType.ORGANISATION) {
          await onSuccess(user.uuid);
        }
      };

      const queuedSave = saveQueueRef.current.then(runSave, runSave);
      saveQueueRef.current = queuedSave.then(
        () => undefined,
        () => undefined
      );

      await queuedSave;
    },
    [onSuccess, ownerType, updateUserProfile, user]
  );

  const onSubmit = async (data: TUserForm) => {
    await saveProfile(data, true);
  };

  return (
    <FormCard2
      schema={userFormSchema}
      showAccentBar
      options={{
        values: defaultValues,
        defaultValues,
        mode: "onSubmit",
        reValidateMode: "onChange",
      }}
      onSubmit={onSubmit}
      title="Your Details"
      description={
        <div className="space-y-3">
          <p className="text-base font-semibold text-foreground">
            Just 3 details to protect your purchase
          </p>
          <p>
            We need your name and a contact email so we can send your SolarCloud
            documents and important account or security alerts.
          </p>
          <p>
            We won’t use this email for marketing unless you choose to hear from
            us.
          </p>
        </div>
      }
    >
      {(form) => (
        <>
          <UserFormEffects
            form={form}
            saveProfile={saveProfile}
            defaultValues={defaultValues}
            userUuid={user?.uuid}
            isLoadingUser={isLoadingUser}
          />
          <FormField
            control={form.control}
            name="givenName"
            render={({ field }) => {
              const isFilled = typeof field.value === "string" && field.value.trim().length > 0;
              return (
                <FormItem>
                  <FormLabel>
                    First name <span className="text-brand-orange-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <UserRound className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                      <Input
                        placeholder="John"
                        autoComplete="given-name"
                        {...field}
                        className={cn(
                          "h-11 rounded-lg border-brand-neutral-700 bg-brand-neutral-800/80 pl-10 pr-10 text-sm focus:border-brand-orange-500 focus:ring-brand-orange-500/30 focus-visible:border-brand-orange-500 focus-visible:ring-brand-orange-500/30",
                          isFilled && "border-emerald-500/40"
                        )}
                      />
                      {isFilled && (
                        <Check className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-400 pointer-events-none" strokeWidth={2.5} />
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="familyName"
            render={({ field }) => {
              const isFilled = typeof field.value === "string" && field.value.trim().length > 0;
              return (
                <FormItem>
                  <FormLabel>
                    Last name <span className="text-brand-orange-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <UserRound className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                      <Input
                        placeholder="Kennedy"
                        autoComplete="family-name"
                        {...field}
                        className={cn(
                          "h-11 rounded-lg border-brand-neutral-700 bg-brand-neutral-800/80 pl-10 pr-10 text-sm focus:border-brand-orange-500 focus:ring-brand-orange-500/30 focus-visible:border-brand-orange-500 focus-visible:ring-brand-orange-500/30",
                          isFilled && "border-emerald-500/40"
                        )}
                      />
                      {isFilled && (
                        <Check className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-400 pointer-events-none" strokeWidth={2.5} />
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => {
              const isValidEmail = paymentDetailsSchema.shape.email.safeParse(field.value).success;
              return (
                <FormItem>
                  <FormLabel>
                    Contact email <span className="text-brand-orange-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                      <Input
                        type="email"
                        placeholder="john@example.com"
                        autoComplete="email"
                        {...field}
                        className={cn(
                          "h-11 rounded-lg border-brand-neutral-700 bg-brand-neutral-800/80 pl-10 pr-10 text-sm focus:border-brand-orange-500 focus:ring-brand-orange-500/30 focus-visible:border-brand-orange-500 focus-visible:ring-brand-orange-500/30",
                          isValidEmail && "border-emerald-500/40"
                        )}
                      />
                      {isValidEmail && (
                        <Check className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-400 pointer-events-none" strokeWidth={2.5} />
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <div className="flex items-center gap-2.5 rounded-lg bg-brand-orange-500/10 border border-brand-orange-500/20 px-3.5 py-2.5 text-xs text-brand-orange-500">
            <Lock className="h-4 w-4 shrink-0" />
            <span>Required for your purchase and security alerts.</span>
          </div>
          <FormField
            control={form.control}
            name="phoneNumber.number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone number (optional)</FormLabel>
                <FormControl>
                  <CustomPhoneInput
                    value={field.value ?? ""}
                    onChange={field.onChange}
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Helpful if we ever need to contact you urgently about your account.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </FormCard2>
  );
}

function UserFormEffects<TFormValues extends FieldValues>({
  form,
  saveProfile,
  defaultValues,
  userUuid,
  isLoadingUser,
}: {
  form: UseFormReturn<TFormValues>;
  saveProfile: (data: TFormValues, syncOwner: boolean) => Promise<void>;
  defaultValues: TFormValues;
  userUuid?: string;
  isLoadingUser: boolean;
}) {
  const setGatekeeperHandler = usePaymentGatekeeperStore(
    (state) => state.setHandler
  );
  const setPaymentDetailsValidity = usePaymentGatekeeperStore(
    (state) => state.setPaymentDetailsValidity
  );
  const hydratedUserRef = useRef<string | null>(null);
  const persistedSnapshotRef = useRef<string>("");
  const watchedValues = useWatch({ control: form.control });
  const createSnapshot = useCallback(
    (values: TFormValues) => JSON.stringify(values),
    []
  );

  // Initialize immediately with defaultValues or form values on mount
  useEffect(() => {
    const initialValues = {
      ...(defaultValues || {}),
      ...(form.getValues() || {}),
    };
    setPaymentDetailsValidity(
      getPaymentDetailsValidity(initialValues as Partial<TFormValues>)
    );
  }, [defaultValues, form, setPaymentDetailsValidity]);

  useEffect(() => {
    if (isLoadingUser || !userUuid) {
      return;
    }

    if (hydratedUserRef.current === userUuid) {
      return;
    }

    const values = form.getValues();

    if (!values || Object.keys(values).length === 0) {
      form.reset(defaultValues);
    } else {
      if (!values['givenName']) form.setValue('givenName' as any, (defaultValues as any).givenName, { shouldDirty: false, shouldTouch: false });
      if (!values['familyName']) form.setValue('familyName' as any, (defaultValues as any).familyName, { shouldDirty: false, shouldTouch: false });
      if (!values['email']) form.setValue('email' as any, (defaultValues as any).email, { shouldDirty: false, shouldTouch: false });
      const phone = (values as any).phoneNumber || {};
      const defaultPhone = (defaultValues as any).phoneNumber || {};
      if (!phone.number && defaultPhone.number) form.setValue('phoneNumber.number' as any, defaultPhone.number, { shouldDirty: false, shouldTouch: false });
    }

    hydratedUserRef.current = userUuid;
    const latestValues = form.getValues();
    persistedSnapshotRef.current = createSnapshot(latestValues);
    setPaymentDetailsValidity(
      getPaymentDetailsValidity(latestValues as Partial<TFormValues>)
    );
  }, [createSnapshot, defaultValues, form, isLoadingUser, setPaymentDetailsValidity, userUuid]);

  useDebouncedAutosave({
    form,
    delay: 600,
    enabled: !isLoadingUser && !!userUuid,
    onSave: async (values) => {
      const snapshot = createSnapshot(values);
      if (
        !paymentDetailsSchema.safeParse(values).success ||
        snapshot === persistedSnapshotRef.current
      ) {
        return;
      }

      await saveProfile(values, false);

      const savedValues = form.getValues();
      persistedSnapshotRef.current = createSnapshot(savedValues);
      form.reset(savedValues, { keepValues: true, keepTouched: true });
    },
  });

  useEffect(() => {
    const currentValues = form.getValues();
    const effectiveValues = {
      ...(defaultValues || {}),
      ...(currentValues || {}),
      ...(watchedValues || {}),
    };
    setPaymentDetailsValidity(
      getPaymentDetailsValidity(effectiveValues as Partial<TFormValues>)
    );
  }, [defaultValues, form, setPaymentDetailsValidity, watchedValues]);

  const gatekeeperHandler = useCallback(async () => {
    const isValid = await form.trigger(
      ["givenName", "familyName", "email"] as any,
      { shouldFocus: false }
    );

    const latestValidity = getPaymentDetailsValidity(
      form.getValues() as Partial<TFormValues>
    );
    setPaymentDetailsValidity(latestValidity);

    if (!isValid || !isPaymentDetailsComplete(latestValidity)) {
      scrollToFirstInvalidField(form);
      return false;
    }

    await saveProfile(form.getValues(), true);
    const latestValues = form.getValues();
    persistedSnapshotRef.current = createSnapshot(latestValues);
    form.reset(latestValues, { keepValues: true, keepTouched: true });
    return true;
  }, [createSnapshot, form, saveProfile, setPaymentDetailsValidity]);

  useEffect(() => {
    setGatekeeperHandler(gatekeeperHandler);

    return () => {
      setGatekeeperHandler(null);
    };
  }, [gatekeeperHandler, setGatekeeperHandler]);

  return null;
}
